import { z } from 'zod';
/**
 * Market research, spelled once (ADR-0134, proposal 0009).
 *
 * The lists here are the closed sets the database checks
 * (migration 0105) and the screens name. A criterion outside them is one
 * the coverage and the agent brief cannot count, so it is refused rather
 * than shown.
 */
/** The eleven deep-scan criteria (Grow Global module 2), in the order a report reads them. */
export const DEEP_SCAN_CRITERIA = [
    'Domestic production',
    'Consumption and market potential',
    'Direct and hidden barriers',
    'Cultural distance',
    'Competitors and concentration',
    'Distribution structure',
    'Access to channels',
    'Market trends',
    'Treatment of foreign companies and goods',
    'End-consumer requirements',
    'Buyer-specific requirements',
];
export const PESTLE = ['political', 'economic', 'social', 'technological', 'legal', 'environmental'];
/**
 * How far a finding can be trusted, as a word the report counts first.
 * `sourced` names its source; `estimate` is ours and says so; `not_found`
 * is written rather than guessed.
 */
export const PROVENANCE = ['sourced', 'inference', 'judgement', 'unverified', 'estimate', 'not_found'];
export const CONFIDENCE = ['high', 'medium', 'low'];
export const MARKET_STAGES = ['screened', 'deep', 'go', 'not_now'];
/** Six routes to market, each scored on the same eight criteria. */
export const ENTRY_ROUTES = [
    { key: 'direct_to_restaurants', label: 'Direct to restaurants' },
    { key: 'distributor', label: 'Distributor' },
    { key: 'e_commerce', label: 'E-commerce' },
    { key: 'agent', label: 'Agent' },
    { key: 'direct_to_retail', label: 'Direct to retail' },
    { key: 'contract_manufacturing', label: 'Contract manufacturing' },
];
/** 1 to 5, where 5 is always attractive: a cheap route scores high on cost to set up. */
export const ENTRY_CRITERIA = [
    { key: 'speed', label: 'Speed to market' },
    { key: 'setup_cost', label: 'Cost to set up' },
    { key: 'logistics', label: 'Logistics' },
    { key: 'risk', label: 'Risk' },
    { key: 'control', label: 'Control over the sale' },
    { key: 'knowledge', label: 'Market knowledge needed' },
    { key: 'profitability', label: 'Profitability' },
    { key: 'reaches_chefs', label: 'Reaches chefs directly' },
];
export const COMPETITOR_CRITERIA = [
    'Where they sell',
    'Staff',
    'Revenue',
    'Main products',
    'Certificates',
    'Price of the equivalent',
    'Sales channels',
    'Advertising channels',
    'Strengths',
    'Weaknesses',
    'Comments',
];
export const SWOT_QUADRANTS = ['strengths', 'weaknesses', 'opportunities', 'threats'];
/** A finding older than this is stale, and a report shows its oldest row's date (A8). */
export const RESEARCH_STALE_DAYS = 90;
/**
 * The open-data sources a criterion is read from before the grounded model
 * is asked (ADR-0132 §3). Each is a manifest under `ledger/manifests/
 * executive`, keyless, and refused until a superadmin admits it; the code
 * that calls them is `apps/work-api/src/open-data.ts`.
 *
 * Trade is read from Eurostat's Comext for an EU reporter and from UN
 * Comtrade for the rest: a market with neither is not asked. The codes are
 * the reporters' own — Comext takes ISO-2, Comtrade its numeric M49 code.
 */
export const TRADE_REPORTERS = {
    DK: { eurostat: 'DK' }, SE: { eurostat: 'SE' }, FI: { eurostat: 'FI' }, DE: { eurostat: 'DE' }, AT: { eurostat: 'AT' },
    NL: { eurostat: 'NL' }, BE: { eurostat: 'BE' }, LU: { eurostat: 'LU' }, FR: { eurostat: 'FR' }, ES: { eurostat: 'ES' },
    IT: { eurostat: 'IT' }, PT: { eurostat: 'PT' }, EE: { eurostat: 'EE' }, PL: { eurostat: 'PL' },
    NO: { comtrade: 579 }, IS: { comtrade: 352 }, CH: { comtrade: 757 }, UA: { comtrade: 804 }, GB: { comtrade: 826 },
};
/** The HS6 codes our products trade under, as the Comext manifest names them. */
export const TRADE_PRODUCTS = [
    { hs: '070320', label: 'fresh garlic' },
    { hs: '071290', label: 'dried garlic and other dried vegetables' },
    { hs: '070310', label: 'fresh onion' },
    { hs: '071220', label: 'dried onion' },
    { hs: '200599', label: 'prepared vegetables' },
    { hs: '210390', label: 'sauces and condiments' },
];
/**
 * Statistics Denmark's StatBank, for the Danish market only: the table and
 * the variables each criterion is read with, checked against
 * api.statbank.dk/v1/tableinfo/<table> before the manifest was signed.
 * A table that answers nothing yields "not found", never a number.
 */
export const STATBANK_QUERIES = [
    {
        // Sales to food service by customer group, DKK million: who buys
        // (restaurants and cafés against canteens and institutions).
        criterion: 'Distribution structure',
        table: 'OEKO88',
        title: 'Statistics Denmark OEKO88, sales to food service by customer group',
        variables: [
            { code: 'VARTYP', values: ['10'] },
            { code: 'KUNDEGRP', values: ['000', '400', '100', '200', '300', '500'] },
            { code: 'MÆNGDE6', values: ['30'] },
            { code: 'Tid', values: ['*'] },
        ],
    },
    {
        // The turnover index of food and beverage service activities (NACE
        // I56), seasonally adjusted, month by month: the trend of the trade.
        criterion: 'Market trends',
        table: 'SPOO1M',
        title: 'Statistics Denmark SPOO1M, turnover index of food and beverage service activities',
        variables: [
            { code: 'ERHVERV', values: ['I56'] },
            { code: 'SAESON2', values: ['SÆSON'] },
            { code: 'Tid', values: ['*'] },
        ],
    },
];
/** The target gross margin a derived price is set at, until margins are set per channel (C6). */
export const TARGET_MARGIN = 0.45;
export const isDeepScanCriterion = (v) => typeof v === 'string' && DEEP_SCAN_CRITERIA.includes(v);
export const isEntryRoute = (v) => typeof v === 'string' && ENTRY_ROUTES.some((r) => r.key === v);
export const isEntryCriterion = (v) => typeof v === 'string' && ENTRY_CRITERIA.some((c) => c.key === v);
export const isProvenance = (v) => typeof v === 'string' && PROVENANCE.includes(v);
/** The route key for a route's label, as the research writes it. */
export const entryRouteKey = (label) => ENTRY_ROUTES.find((r) => r.label.toLowerCase() === label.trim().toLowerCase())?.key;
/**
 * The procedures one department may ask another to run (migration 0107).
 *
 * A request that names one is run in code by the receiver, not by a
 * general agent loop, so what comes back has a shape a record can take.
 */
export const REQUEST_PROCEDURES = [
    'research_market',
    // A person's suggestion for a page, written up by Norum Tech as a spec for
    // the superadmin (migration 0132).
    'page_suggestion',
    // Outbound asks Executive to find the manufacturers of a product in one
    // country, each from its own site (migration 0159, ADR-0162).
    'research_suppliers',
    // Outbound asks Executive to find the distributors who could buy, in one
    // country, each from its own site (migration 0176, ADR-0176).
    'research_distributors',
];
/**
 * A market's own sources (ADR-0141): what a page or feed a person attached
 * in `ledger/departments/<department>/sources.yaml` is, and what it is read
 * for. A `kind` is the person's word for it; `reads_for` decides who reads
 * it: a signal source by the department's prospecting agent, a criteria
 * source by Nia inside `research_market`.
 */
export const SOURCE_KINDS = ['news', 'blog', 'statistics', 'register', 'tax_office', 'trade_body', 'other'];
/**
 * What a news or blog source may say: a place opened, closed, a chef moved;
 * or, of a country the product grows in, that its harvest is at risk
 * (0156) — a heatwave, a drought, a frost — which is a reason to ask a
 * supplier and never a point of fit.
 */
export const SOURCE_SIGNALS = ['opening', 'closure', 'chef_move', 'supply_risk'];
const sourceId = z.string().regex(/^[a-z][a-z0-9_]*$/).max(60);
const httpsUrl = z.string().url().max(2000).refine((u) => u.startsWith('https://'), 'a source is read over https');
/** A page source carried in a research ask: the URL the ledger names, the manifest that reads it, and the criteria it answers. */
export const researchSourceSchema = z
    .object({
    id: sourceId,
    title: z.string().min(1).max(200),
    url: httpsUrl,
    tool: z.string().regex(/^[a-z][a-z_]*\.[a-z][a-z0-9_]*$/),
    criteria: z.array(z.enum(DEEP_SCAN_CRITERIA)).min(1).max(DEEP_SCAN_CRITERIA.length),
})
    .strict();
/** What a market's research asks: one country, optionally one product, the criteria still open, and the market's own page sources. */
export const researchAskSchema = z
    .object({
    procedure: z.literal('research_market'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    country: z.string().min(1).max(80),
    product: z.string().regex(/^[a-z][a-z_]*$/).nullable(),
    criteria: z.array(z.enum(DEEP_SCAN_CRITERIA)).min(1).max(DEEP_SCAN_CRITERIA.length),
    sources: z.array(researchSourceSchema).max(20).default([]),
})
    .strict();
/**
 * What comes back: one finding per criterion asked, each either sourced —
 * with the pages it rests on, as the search reported them, and those of
 * them the platform read and found holding it — or not found.
 * There is no third kind. A model's answer with no page behind it is not
 * a finding (ADR-0132).
 */
export const researchFindingSchema = z.discriminatedUnion('provenance', [
    z
        .object({
        criterion: z.enum(DEEP_SCAN_CRITERIA),
        provenance: z.literal('sourced'),
        finding: z.string().min(1).max(4000),
        sources: z
            .array(z.object({ url: z.string().regex(/^https:\/\/\S+$/).max(2000), title: z.string().max(300).nullable() }).strict())
            .min(1)
            .max(20),
        pestle: z.array(z.enum(PESTLE)).max(PESTLE.length),
        /**
         * The pages the platform itself read that hold this finding
         * (ADR-0132 §2, ADR-0151): an open-data answer or a ledger page it
         * read, or a cited page whose text carries every key figure the
         * finding states. Empty or absent, the finding is recorded
         * `unverified` with its pages, never `sourced`.
         */
        checked: z.array(z.string().regex(/^https:\/\/\S+$/).max(2000)).max(20).optional(),
    })
        .strict(),
    z
        .object({
        criterion: z.enum(DEEP_SCAN_CRITERIA),
        provenance: z.literal('not_found'),
        finding: z.string().min(1).max(4000),
    })
        .strict(),
]);
export const researchAnswerSchema = z
    .object({
    procedure: z.literal('research_market'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    product: z.string().regex(/^[a-z][a-z_]*$/).nullable(),
    researchedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    model: z.string().min(1).max(100),
    findings: z.array(researchFindingSchema).max(DEEP_SCAN_CRITERIA.length),
})
    .strict();
/**
 * What a supplier search looks for on a manufacturer's own pages (ADR-0162):
 * the ten things the owner named, each kept as the answer to the supplier
 * question it belongs to (the fourteen in `SUPPLIER_FIELDS`). A product's
 * range and its specifications are one answer, `spec`.
 */
export const SUPPLIER_DATA_POINTS = [
    { key: 'spec', field: 'spec', label: 'Product range and specifications' },
    { key: 'formats', field: 'formats', label: 'Packaging formats' },
    { key: 'certifications', field: 'certifications', label: 'Certifications' },
    { key: 'private_label', field: 'private_label', label: 'Private-label or contract manufacturing' },
    { key: 'moq', field: 'moq', label: 'Minimum order quantity' },
    { key: 'trial_batch', field: 'trial_cost_moq', label: 'Trial batch available' },
    { key: 'lead_time', field: 'lead_time', label: 'Production lead time' },
    { key: 'price', field: 'purchase_price', label: 'Approximate prices' },
    { key: 'payment_terms', field: 'payment_terms', label: 'Payment terms' },
    { key: 'delivery', field: 'delivery', label: 'Delivery options to Sweden' },
];
export const SUPPLIER_DATA_POINT_KEYS = SUPPLIER_DATA_POINTS.map((p) => p.key);
/**
 * Where a supplier search looks, each with the name Nia searches it by.
 * The first wave (the owner, 2026-10-02): Poland, Lithuania and Spain. The
 * Balkans are the second wave (the owner, 2026-10-03, ADR-0170). Adding a
 * country here is the whole change, because a search is a country code and
 * its name; whether a maker found there is written to stays a person's
 * decision, country by country, on the platform.
 */
export const SUPPLIER_COUNTRY_NAMES = {
    PL: 'Poland',
    LT: 'Lithuania',
    ES: 'Spain',
    AL: 'Albania',
    BA: 'Bosnia and Herzegovina',
    BG: 'Bulgaria',
    GR: 'Greece',
    HR: 'Croatia',
    ME: 'Montenegro',
    MK: 'North Macedonia',
    RO: 'Romania',
    RS: 'Serbia',
    SI: 'Slovenia',
};
export const SUPPLIER_SEARCH_COUNTRIES = Object.keys(SUPPLIER_COUNTRY_NAMES);
/** The Balkan countries, for a search a person asks of "the Balkans". */
export const BALKAN_SUPPLIER_COUNTRIES = ['AL', 'BA', 'BG', 'GR', 'HR', 'ME', 'MK', 'RO', 'RS', 'SI'];
const pageUrl = z.string().regex(/^https:\/\/\S+$/).max(2000);
/** What a supplier search asks: one country, the products, and how many makers at most. */
export const supplierResearchAskSchema = z
    .object({
    procedure: z.literal('research_suppliers'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    country: z.string().min(1).max(80),
    products: z.array(z.string().regex(/^[a-z][a-z_]*$/)).min(1).max(4),
    maxCandidates: z.number().int().min(1).max(12),
})
    .strict();
/**
 * One manufacturer, as the pages Nia read describe it. A maker rests on its
 * own site; each point rests on the page it was read on; and a contact's
 * address is in the answer only when it is written on a page that was read.
 * What a page does not say is absent, never filled in.
 */
export const supplierCandidateSchema = z
    .object({
    name: z.string().min(2).max(200),
    website: pageUrl,
    /** Every page read for it, the first being the site's own. */
    pages: z.array(pageUrl).min(1).max(4),
    points: z
        .array(z
        .object({
        key: z.enum(SUPPLIER_DATA_POINT_KEYS),
        answer: z.string().min(1).max(600),
        url: pageUrl,
        /** Words copied from that page, which the platform found there before keeping the answer. */
        quote: z.string().min(8).max(300),
    })
        .strict())
        .max(SUPPLIER_DATA_POINTS.length),
    contact: z
        .object({
        name: z.string().min(1).max(200).nullable(),
        role: z.string().min(1).max(200).nullable(),
        address: z.string().min(3).max(320),
        url: pageUrl,
    })
        .strict()
        .nullable(),
    /** The pages Google cited when the maker was found, for a person to open. */
    foundOn: z.array(pageUrl).max(10),
})
    .strict();
export const supplierResearchAnswerSchema = z
    .object({
    procedure: z.literal('research_suppliers'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    products: z.array(z.string()).min(1).max(4),
    researchedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    model: z.string().min(1).max(100),
    candidates: z.array(supplierCandidateSchema).max(12),
    /** Why a maker named by the search was not kept, in words: a page that would not read, one the scanner held. */
    skipped: z.array(z.string().max(300)).max(40),
})
    .strict();
/**
 * Who a distributor search looks for (ADR-0176): the channels a buyer of a
 * small European food maker sells into. HoReCa first, because the owner's
 * target customer is the distributor who supplies restaurants.
 */
export const DISTRIBUTOR_CHANNELS = ['horeca', 'food_service', 'retail', 'wholesale', 'import'];
export const DISTRIBUTOR_CHANNEL_WORDS = {
    horeca: 'hotels, restaurants and cafés (HoReCa)',
    food_service: 'food service and catering',
    retail: 'retail and delicatessen',
    wholesale: 'cash-and-carry and wholesale',
    import: 'import of specialty foods',
};
/**
 * What a distributor's own pages are read for (ADR-0176). No contact: a
 * distributor taken as a prospect is resolved through the plan's own Places
 * search, and its site is read for a contact there, under the plan's grant.
 */
export const DISTRIBUTOR_DATA_POINTS = [
    { key: 'channels', label: 'Who they sell to' },
    { key: 'categories', label: 'Product categories carried' },
    { key: 'brands', label: 'Brands or producers carried' },
    { key: 'regions', label: 'Regions or cities served' },
    { key: 'imports', label: 'Imports from abroad' },
    { key: 'new_suppliers', label: 'How a new supplier is taken on' },
];
export const DISTRIBUTOR_DATA_POINT_KEYS = DISTRIBUTOR_DATA_POINTS.map((p) => p.key);
/** What a distributor search asks: one country, the channels, and how many at most. */
export const distributorResearchAskSchema = z
    .object({
    procedure: z.literal('research_distributors'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    country: z.string().min(1).max(80),
    channels: z.array(z.enum(DISTRIBUTOR_CHANNELS)).min(1).max(DISTRIBUTOR_CHANNELS.length),
    maxCandidates: z.number().int().min(1).max(12),
})
    .strict();
/** One distributor, as its own pages describe it; every point rests on a page it quotes. */
export const distributorCandidateSchema = z
    .object({
    name: z.string().min(2).max(200),
    website: pageUrl,
    pages: z.array(pageUrl).min(1).max(4),
    points: z
        .array(z
        .object({
        key: z.enum(DISTRIBUTOR_DATA_POINT_KEYS),
        answer: z.string().min(1).max(600),
        url: pageUrl,
        quote: z.string().min(8).max(300),
    })
        .strict())
        .max(DISTRIBUTOR_DATA_POINTS.length),
    foundOn: z.array(pageUrl).max(10),
})
    .strict();
export const distributorResearchAnswerSchema = z
    .object({
    procedure: z.literal('research_distributors'),
    marketCode: z.string().regex(/^[A-Z]{2}$/),
    channels: z.array(z.enum(DISTRIBUTOR_CHANNELS)).min(1).max(DISTRIBUTOR_CHANNELS.length),
    researchedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    model: z.string().min(1).max(100),
    candidates: z.array(distributorCandidateSchema).max(12),
    skipped: z.array(z.string().max(300)).max(40),
})
    .strict();
/**
 * What ends a cadence (ADR-0136 §3, migration 0108). Recorded, never
 * inferred later: each is the rule that fired, and when.
 */
export const CADENCE_STOPS = ['replied', 'out_of_office', 'bounced', 'price_list_visit', 'lost', 'silence', 'suppressed', 'erased', 'introduced'];
/** A plan's gaps, in days: email → LinkedIn, LinkedIn → call. */
export const CADENCE_GAPS = { '3/7': [3, 7], '2/5': [2, 5], '5/14': [5, 14] };
/** Days of silence after the last step that end a cadence. */
export const CADENCE_SILENCE_DAYS = 30;
/** Why a prospect was lost, and the objection a reply is filed under (R10). */
export const LOST_REASONS = ['price', 'already_supplied', 'not_now', 'wrong_person', 'other'];
export const SAMPLE_STATES = ['asked', 'sent', 'ordered', 'no_order'];
/**
 * The fourteen things a supplier is asked before a first order (the
 * validation brief; migration 0109), in the order the brief asks them. The
 * question is what "ask them for what's missing" puts in the draft.
 */
export const SUPPLIER_FIELDS = [
    { key: 'purchase_price', label: 'Purchase price', question: 'Your current price per kilogram for each format, and until when it is valid' },
    { key: 'moq', label: 'Minimum order', question: 'Your minimum order quantity' },
    { key: 'trial_cost_moq', label: 'Trial order', question: 'What a first trial order at the minimum would cost, delivered' },
    { key: 'formats', label: 'Formats', question: 'The formats and pack sizes you supply' },
    { key: 'lead_time', label: 'Lead time', question: 'Your lead time from order to dispatch' },
    { key: 'delivery', label: 'Delivery', question: 'What delivery to our market would cost and how long it takes' },
    { key: 'payment_terms', label: 'Payment terms', question: 'Your payment terms' },
    { key: 'shelf_life', label: 'Shelf life and storage', question: 'The shelf life and storage conditions of each product, and which need refrigeration' },
    { key: 'spec', label: 'Specification', question: 'The specification sheet for each product, including the origin of the garlic and onion' },
    { key: 'certifications', label: 'Certifications', question: 'The certifications you hold (for example IFS, BRC, organic)' },
    { key: 'private_label', label: 'Private label', question: 'Whether you supply under a buyer’s own label, and from what quantity' },
    { key: 'sample_cost', label: 'Samples', question: 'What samples cost and how quickly they can be sent' },
    { key: 'capacity', label: 'Capacity', question: 'Your monthly capacity for these products' },
    { key: 'repeatability', label: 'Repeat supply', question: 'Whether the same quality can be supplied repeatedly through the year' },
];
export const isSupplierField = (v) => typeof v === 'string' && SUPPLIER_FIELDS.some((f) => f.key === v);
/** The answers a price list states, which stop being confirmed the day after the list's validity date. */
export const PRICE_LIST_FIELDS = ['purchase_price', 'moq', 'trial_cost_moq'];
/**
 * A strategy an assistant proposes (ADR-0153, Phase C): what a lead would
 * otherwise type into the set-up wizard, in its words. Nothing runs from it;
 * a lead sets up from it, and the wizard's own checks and the superadmin's
 * approval decide the rest.
 */
export const strategyBodySchema = z
    .object({
    segment: z.string().min(2).max(120),
    looksFor: z.enum(['customers', 'suppliers', 'competitors']).optional(),
    rules: z.array(z.object({ label: z.string().min(2).max(120), points: z.number().int().min(-100).max(100) }).strict()).max(12).optional(),
    cities: z.array(z.string().min(2).max(80)).max(20).optional(),
    regions: z.array(z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/)).max(20).optional(),
    perDay: z.number().int().min(1).max(50).optional(),
    notes: z.string().max(1200).optional(),
})
    .strict();
//# sourceMappingURL=research.js.map